import { create } from "zustand";
import { AcceptChangesFromCsvDto } from "service/api/estabrookSchemas";

type CsvUploadStatus =
  | "idle"
  | "processing"
  | "ready-for-review"
  | "applying-changes"
  | "error";
type CsvImportStatus = "success" | "updating";

// Using the same type as LocationSyncFromCsvResponse for compatibility
interface ReviewData {
  newAddressAndCustomer: number;
  existingAddressNewCustomer: number;
  updateCustomerOnExistingAddress: number;
  updateAddressOfExistingCustomer: number;
  existingCustomerNewAddress: number;
  validationError: number;
  validationErrors: ValidationError[];
}

interface ValidationError {
  row: number;
  errors: string[];
}

interface CsvUploadState {
  status: CsvUploadStatus;
  progress: number;
  message: string;
  showTooltip: boolean;
  reviewData: ReviewData | null;
  isProgressPaused: boolean; // Track if progress updates are paused
  wasCancelled: boolean; // Track if current session was cancelled by user
  wasUpdate: boolean; // Track if this was an update (not cancellation)
  isModalOpen: boolean; // Track if CSV import modal is open
  modalStatus: CsvImportStatus | undefined; // Track modal-specific status
  onUpdateCustomersCallback:
    | ((selections: AcceptChangesFromCsvDto) => void)
    | null; // Callback for updating customers
  isAcceptingFromMainPage: boolean; // Track if accept operation is from main page
  uploadSessionId: string | null; // Track current upload session to ignore stale progress updates

  // Actions
  setUploadState: (state: {
    status: CsvUploadStatus;
    progress: number;
    message: string;
    showTooltip: boolean;
    reviewData?: ReviewData | null;
  }) => void;

  setProgress: (percentage: number, message?: string) => void;

  startUpload: (message?: string) => void;

  setReadyForReview: (reviewData: ReviewData, message?: string) => void;

  setApplyingChanges: (message?: string) => void;

  completeUpload: () => void;

  errorUpload: (message?: string) => void;

  hideTooltip: () => void;

  cancelUpload: () => void;

  resetVisualState: () => void;

  pauseProgressUpdates: () => void;

  resumeProgressUpdates: () => void;

  setAcceptingFromMainPage: (accepting: boolean) => void;

  completeUploadWithSuccess: (onSuccess?: () => void) => void;

  // Modal actions
  openModal: (
    reviewData: ReviewData,
    status?: CsvImportStatus,
    callback?: (selections: AcceptChangesFromCsvDto) => void,
  ) => void;

  closeModal: () => void;

  setModalStatus: (status: CsvImportStatus | undefined) => void;

  setUpdateCustomersCallback: (
    callback: (selections: AcceptChangesFromCsvDto) => void,
  ) => void;

  callUpdateCustomersCallback: (selections: AcceptChangesFromCsvDto) => void;

  // Computed getters
  isUploading: boolean;
  isReadyForReview: boolean;
}

const useCsvUploadStore = create<CsvUploadState>((set, get) => ({
  status: "idle",
  progress: 0,
  message: "Processing Customer Upload",
  showTooltip: false,
  reviewData: null,
  isUploading: false,
  isReadyForReview: false,
  isProgressPaused: false,
  wasCancelled: false,
  wasUpdate: false,
  isModalOpen: false,
  modalStatus: undefined,
  onUpdateCustomersCallback: null,
  isAcceptingFromMainPage: false,
  uploadSessionId: null,

  setUploadState: (state) => set(state),

  setProgress: (percentage, message) => {
    const currentState = get();

    // If progress updates are paused, ignore the update
    if (currentState.isProgressPaused) {
      return;
    }

    // If the upload was cancelled, ignore progress updates to prevent stale updates
    // from interfering with new uploads
    if (currentState.wasCancelled && !currentState.isAcceptingFromMainPage) {
      return;
    }

    // Don't clear auto-hide timer during progress updates
    // Let it run its course to maintain stable tooltip behavior

    // Clamp percentage and only update if changed significantly to reduce excessive re-renders
    const newProgress = Math.min(Math.max(percentage, 0), 100);

    // Skip minor updates (less than 1%) unless it's 0% or 100%
    if (
      Math.abs(newProgress - currentState.progress) < 1 &&
      currentState.progress > 0 &&
      newProgress < 100
    ) {
      return;
    }

    // Update progress without affecting tooltip visibility
    const newState = {
      ...currentState,
      progress: newProgress,
      ...(message && { message }),
      // Don't force showTooltip - let sidebar logic handle tooltip visibility
      status: "processing" as CsvUploadStatus,
      isUploading: true,
      isReadyForReview: false,
      // Don't clear autoHideTimer during progress - let it run its course
    };

    set(newState);
  },

  startUpload: (message = "Processing Customer Upload") => {
    // Generate a new session ID for this upload to track progress updates
    const newSessionId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    set({
      status: "processing",
      progress: 0,
      message,
      showTooltip: true, // Show tooltip immediately, sidebar will handle timing
      reviewData: null,
      isUploading: true,
      isReadyForReview: false,
      isProgressPaused: false, // Ensure progress updates are not paused
      wasCancelled: false, // Reset cancellation flag for new upload
      wasUpdate: false, // Reset update flag for new upload
      uploadSessionId: newSessionId, // Track this upload session
    });
  },

  setReadyForReview: (reviewData, message = "Customer Upload Ready") => {
    set({
      status: "ready-for-review",
      progress: 100,
      message,
      showTooltip: true, // Sidebar will handle auto-hide timing
      reviewData,
      isUploading: false,
      isReadyForReview: true,
    });
  },

  setApplyingChanges: (message = "Applying your changes") => {
    set({
      status: "applying-changes",
      progress: 100,
      message,
      showTooltip: true, // Show tooltip during applying state
      isUploading: false,
      isReadyForReview: false,
      wasCancelled: false,
      wasUpdate: true, // Mark as update operation
    });
  },

  completeUpload: () => {
    set({
      status: "idle",
      progress: 100,
      showTooltip: false,
      reviewData: null,
      isUploading: false,
      isReadyForReview: false,
      wasCancelled: false, // Reset cancellation flag when upload completes
      wasUpdate: false, // Reset update flag when upload completes
      isModalOpen: false, // Close modal when upload completes
      modalStatus: undefined,
      isAcceptingFromMainPage: false, // Reset main page flag
      uploadSessionId: null, // Clear session ID when completing
    });
  },

  errorUpload: (message = "Upload failed") => {
    set({
      status: "error",
      progress: 0,
      message,
      showTooltip: false,
      reviewData: null,
      isUploading: false,
      isReadyForReview: false,
      wasCancelled: false, // Reset on error
      wasUpdate: false, // Reset on error
      uploadSessionId: null, // Clear session ID on error
    });
  },

  hideTooltip: () =>
    set((state) => ({
      ...state,
      showTooltip: false,
      // Keep the computed values as they are since we're just hiding tooltip
    })),

  cancelUpload: () => {
    set({
      status: "idle" as const,
      progress: 0,
      message: "",
      showTooltip: false,
      reviewData: null,
      isUploading: false,
      isReadyForReview: false,
      isProgressPaused: false, // Reset pause state when cancelling
      wasCancelled: true, // Mark that this session was cancelled by user
      wasUpdate: false, // This is a real cancellation, not an update
      isModalOpen: false, // Close modal when cancelling
      modalStatus: undefined,
      isAcceptingFromMainPage: false, // Reset main page flag
      uploadSessionId: null, // Clear session ID when cancelling
    });
  },

  resetVisualState: () => {
    // Reset visual state and mark as cancelled to prevent API overrides
    // BUT track that this was an update (not a cancellation) for WebSocket handling
    set((state) => ({
      ...state,
      status: "idle" as const,
      progress: 0,
      message: "",
      showTooltip: false,
      reviewData: null,
      isUploading: false,
      isReadyForReview: false,
      isProgressPaused: false,
      wasCancelled: true, // Prevent API overrides
      wasUpdate: true, // New flag to track this was an update, not cancellation
    }));
  },

  pauseProgressUpdates: () => {
    set((state) => ({
      ...state,
      isProgressPaused: true,
    }));
  },

  resumeProgressUpdates: () => {
    set((state) => ({
      ...state,
      isProgressPaused: false,
    }));
  },

  openModal: (reviewData, status = "success", callback) => {
    set({
      isModalOpen: true,
      modalStatus: status,
      reviewData,
      onUpdateCustomersCallback: callback || null,
    });
  },

  closeModal: () => {
    set({
      isModalOpen: false,
      modalStatus: undefined,
      onUpdateCustomersCallback: null,
    });
  },

  setModalStatus: (status) => {
    set((state) => ({
      ...state,
      modalStatus: status,
    }));
  },

  setUpdateCustomersCallback: (callback) => {
    set((state) => ({
      ...state,
      onUpdateCustomersCallback: callback,
    }));
  },

  callUpdateCustomersCallback: (selections) => {
    const currentState = get();
    if (currentState.onUpdateCustomersCallback) {
      currentState.onUpdateCustomersCallback(selections);
    }
  },

  setAcceptingFromMainPage: (accepting) => {
    set((state) => ({
      ...state,
      isAcceptingFromMainPage: accepting,
    }));
  },

  completeUploadWithSuccess: (onSuccess) => {
    // Set timestamp to prevent sidebar from immediately re-setting review state
    localStorage.setItem("csvUploadCompleteTime", Date.now().toString());

    // Call the success callback first (for refetch, toast, etc.)
    if (onSuccess) {
      onSuccess();
    }

    // Then reset all state (same as cancelUpload but without the cancelled flags)
    set({
      status: "idle",
      progress: 0,
      message: "",
      showTooltip: false,
      reviewData: null,
      isUploading: false,
      isReadyForReview: false,
      isProgressPaused: false,
      wasCancelled: false,
      wasUpdate: false,
      isModalOpen: false,
      modalStatus: undefined,
      isAcceptingFromMainPage: false,
      uploadSessionId: null, // Clear session ID when completing with success
    });
  },
}));

export default useCsvUploadStore;
